/** biome-ignore-all lint/style/useNamingConvention: GTM event format requirement */
'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { tooltip as Tooltip } from '@/app/_components/Base/Tooltip';
import { Link } from '@/app/_components/common/Link';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import { useUser } from '@/app_lib/providers/UserProvider';
import { trackEvent } from '@/app_lib/services/Gtm/utils';
import { login } from '@/app_lib/services/tinypass/service';
import { cn } from '@/app_lib/utils/utils';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

interface SaveArticleButtonProps {
  className?: string;
  iconClassName?: string;
  articleId: number;
}

export const SaveArticleButton = ({ className, iconClassName, articleId }: SaveArticleButtonProps) => {
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const { optimizely } = useOptimizely();
  const {
    userAccess: { loggedIn, data },
  } = useUser();
  const router = useRouter();

  useEffect(() => {
    if (!(articleId && loggedIn && optimizely)) {
      return;
    }

    const fetchInitialSavedState = async () => {
      try {
        const res = await fetch(`/api/getSavedArticleById/${articleId}`);
        const articleData = await res.json();
        const alreadySaved = articleData.saved;
        setIsSaved(alreadySaved);

        const pendingId = localStorage.getItem('pendingSaveArticleId');
        if (Number(pendingId) === articleId) {
          localStorage.removeItem('pendingSaveArticleId');

          const authMethod = localStorage.getItem('pendingSaveAuthMethod') || 'login';
          localStorage.removeItem('pendingSaveAuthMethod');
          const eventLabel = authMethod === 'registration' ? 'save_registration_complete' : 'save_login_complete';

          trackOptimizelyEvent(optimizely, eventLabel, {
            article_id: articleId,
            timestamp: Date.now(),
            user_id: data?.sub || '',
          });
          trackEvent({
            timestamp: Date.now(),
            eventLabel,
          });

          if (alreadySaved) {
            trackOptimizelyEvent(optimizely, 'save_click', {
              article_id: articleId,
              timestamp: Date.now(),
              user_id: data?.sub || '',
            });
            trackEvent({
              timestamp: Date.now(),
              eventLabel: 'save_click',
            });
          } else {
            await handleSaveToggle();
          }
        }
      } catch {
        setIsSaved(false);
      }
    };

    fetchInitialSavedState();
  }, [articleId, loggedIn, optimizely]);

  const handleSaveToggle = async () => {
    if (!articleId) {
      return;
    }

    if (!loggedIn) {
      localStorage.setItem('pendingSaveArticleId', String(articleId));
      login({
        onLogin: () => {
          router.refresh();
        },
        onLoginDisplayed: () => {
          trackOptimizelyEvent(optimizely, 'save_registration_modal_view', {
            article_id: articleId,
            timestamp: Date.now(),
          });
          trackEvent({
            timestamp: Date.now(),
            eventLabel: 'save_registration_modal_view',
          });
        },
        onLoginSuccess: () => {
          localStorage.setItem('pendingSaveAuthMethod', 'login');
        },
        onRegistrationSuccess: () => {
          localStorage.setItem('pendingSaveAuthMethod', 'registration');
        },
      });
      return;
    }

    const action: 'save' | 'unsave' = isSaved ? 'unsave' : 'save';

    try {
      setIsLoading(true);
      setError(null);
      setShowSuccess(false);

      if (action === 'save') {
        trackOptimizelyEvent(optimizely, 'save_click', {
          articleId,
          is_registered: loggedIn ? 'true' : 'false',
          timestamp: Date.now(),
          user_id: data?.sub || '',
        });
        trackEvent({
          timestamp: Date.now(),
          eventLabel: 'save_click',
        });

        await fetch('/api/saveArticle', {
          body: JSON.stringify({ articleId }),
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
        });

        trackOptimizelyEvent(optimizely, 'save_complete', {
          article_id: articleId,
          timestamp: Date.now(),
          user_id: data?.sub || '',
        });
        trackEvent({
          timestamp: Date.now(),
          eventLabel: 'save_complete',
        });
      } else {
        await fetch('/api/deleteSavedArticle', {
          body: JSON.stringify({ articleId }),
          headers: { 'Content-Type': 'application/json' },
          method: 'DELETE',
        });

        trackOptimizelyEvent(optimizely, 'unsave_article', {
          article_id: articleId,
          timestamp: Date.now(),
          user_id: data?.sub || '',
        });
        trackEvent({
          timestamp: Date.now(),
          eventLabel: 'unsave_article',
        });
      }

      setIsSaved((prev) => !prev);
      setShowSuccess(true);
    } catch {
      setError(`Unable to ${action} article. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const getAriaLabel = () => {
    if (loggedIn && isSaved) {
      return 'Remove from saved articles';
    }
    if (loggedIn) {
      return 'Save article';
    }
    return 'Save article (requires sign in)';
  };

  const successContent = (
    <div className='p-xxs flex flex-col gap-2 typography-default text-primary'>
      <div>
        {isSaved ? (
          <>
            Article saved successfully — find your saved articles in your{' '}
            <Link href='/account/my-content'>My Account</Link> section.
          </>
        ) : (
          'Article removed from saved articles.'
        )}
      </div>
      <ButtonV2
        as='button'
        className='w-full normal-case'
        onClick={() => {
          setShowSuccess(false);
        }}
        variant='primaryOutline'
      >
        Ok, got it
      </ButtonV2>
    </div>
  );

  return (
    <div className='relative font-graphik-compact'>
      <ActionableIcon
        aria-label={getAriaLabel()}
        className={cn('min-w-6 min-h-6 flex items-center justify-center', isLoading && 'cursor-wait', className)}
        data-cy='save-article-button'
        disabled={isLoading}
        onClick={handleSaveToggle}
      >
        {isLoading ? (
          <div className='w-6 h-6 animate-spin rounded-full border-2 border-current border-t-transparent' />
        ) : (
          <Icon
            className={cn(
              'w-6 h-6 transition-colors duration-200',
              isSaved ? 'fill-blue-500 stroke-blue-500' : 'fill-none stroke-current',
              iconClassName,
            )}
            icon='Bookmark'
          />
        )}
      </ActionableIcon>

      {error && (
        <Tooltip
          className='w-[276px]'
          content={<p className='typography-default'>{error}</p>}
          isOpen={error !== null}
          onClose={() => setError(null)}
          showClose={false}
          variant='error'
        />
      )}

      {showSuccess && (
        <Tooltip
          className='w-[276px]'
          content={successContent}
          isOpen={showSuccess}
          onClose={() => {
            setShowSuccess(false);
          }}
          showClose={false}
        />
      )}
    </div>
  );
};
